import { observable, runInAction } from 'mobx';

import { DbKey } from '../adl-gen/common/db';
import * as DB from '../adl-gen/totalinteraction/futurefit/db';
import { CompletionEvent, makeCompletionEventProperties } from '../adl-gen/totalinteraction/futurefit/db/learningevent';
import * as FE from '../adl-gen/totalinteraction/futurefit/frontend';
import {
  NextQuestionResp,
  QuizAnswerQuestionReq,
  SelfAssessmentAnswerQuestionReq
} from '../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../loader/loader';
import { Service } from '../service/service';

import { LoggedIn } from './identity-store';

export class QuizStore {
  /** Response from the server */
  @observable.ref userQuizSummary: MaybeLoading<FE.UserQuizSummary> = {
    kind: 'loading'
  };
  @observable.ref nextQuestion: MaybeLoading<NextQuestionResp> = {
    kind: 'loading'
  };
  @observable.ref workRoleListEntry: MaybeLoading<FE.WorkRoleListEntry> = {
    kind: 'loading'
  };

  @observable currentQuizQuestionId: DbKey<DB.QuizQuestion> | null = null;

  constructor(
    /** Service to interact with the server */
    private readonly service: Service,
    readonly userQuizId: DbKey<DB.UserQuiz>,
    private readonly identity: LoggedIn
  ) {
    this.userQuizSummary = fromPromise(this.getUserQuizSummary());
    this.nextQuestion = fromPromise(this.getNextQuestion(userQuizId));
    this.workRoleListEntry = fromPromise(this.getWorkRoleListEntry(userQuizId));

    ignoreUnused(this.identity);
  }

  getUserQuizSummary = async (): Promise<FE.UserQuizSummary> => {
    const quizzes = (await this.service.queryQuizzesForUser(this.identity.appUserWithDbId.id)).items.filter(
      q => q.userQuizId === this.userQuizId
    );
    if (quizzes.length === 0) {
      throw new Error('UserQuiz ' + this.userQuizId + ' not found');
    }
    return quizzes[0];
  };

  heartbeat = async (): Promise<void> => {
    if (this.currentQuizQuestionId !== null) {
      await this.service.quizQuestionEvent({
        eventType: {
          kind: 'heartbeat'
        },
        userQuizId: this.userQuizId,
        quizQuestionId: this.currentQuizQuestionId
      });
    }
  };

  /// For self-assessment answers, we will pass in the QuizQuestionId,
  /// and the currentQuizQuestionId field is just a placeholder
  /// TODO: Later - make this just one round-trip for self-assessment answers across
  /// TODO: all skills in the competency
  selectAnswer = async (answer: DB.AnswerResponse, quizQuestionId?: DbKey<DB.QuizQuestion>): Promise<void> => {
    if (this.currentQuizQuestionId !== null) {
      // Keep this check for correctness
      await this.service.quizQuestionEvent({
        eventType: {
          kind: 'answerSelected',
          value: answer
        },
        userQuizId: this.userQuizId,
        quizQuestionId: quizQuestionId ? quizQuestionId : this.currentQuizQuestionId
      });
    }
  };

  submitAnswer = async (answer: QuizAnswerQuestionReq): Promise<void> => {
    await this.service.quizAnswerQuestion(answer);
    await this.service.quizQuestionEvent({
      eventType: {
        kind: 'submitted'
      },
      userQuizId: this.userQuizId,
      quizQuestionId: answer.quizQuestionId
    });
    runInAction(() => (this.nextQuestion = fromPromise(this.getNextQuestion(this.userQuizId))));
  };

  submitSelfAssessmentAnswers = async (answers: SelfAssessmentAnswerQuestionReq[]): Promise<void> => {
    /// MUST RUN IN SERIES
    for (const answer of answers) {
      await this.service.selfAssessmentAnswerQuestion(answer);
      await this.service.quizQuestionEvent({
        eventType: {
          kind: 'submitted'
        },
        userQuizId: this.userQuizId,
        quizQuestionId: answer.quizQuestionId
      });
    }
    runInAction(() => (this.nextQuestion = fromPromise(this.getNextQuestion(this.userQuizId))));
    return;
  };

  getNextQuestion = async (userQuizId: DbKey<DB.UserQuiz>): Promise<NextQuestionResp> => {
    const resp = await this.service.getNextQuestion(userQuizId);
    if (resp.kind === 'quiz') {
      if (resp.value.kind === 'nextQuestion') {
        await this.service.quizQuestionEvent({
          eventType: {
            kind: 'shown'
          },
          userQuizId: this.userQuizId,
          quizQuestionId: resp.value.value.quizQuestionId
        });
      }
    } else {
      if (resp.value.kind === 'nextQuestion') {
        /// run this for all the questions retrieved by the API call
        await Promise.all(
          resp.value.value.skillQuestions.map(async q => {
            await this.service.quizQuestionEvent({
              eventType: { kind: 'shown' },
              userQuizId: this.userQuizId,
              quizQuestionId: q.v1
            });
          })
        );
      }
    }
    if (resp.kind === 'quiz') {
      runInAction(() => {
        this.currentQuizQuestionId = resp.value.kind === 'nextQuestion' ? resp.value.value.quizQuestionId : null;
      });
    } else {
      runInAction(() => {
        /// Just pick the first one
        this.currentQuizQuestionId = resp.value.kind === 'nextQuestion' ? resp.value.value.skillQuestions[0].v1 : null;
      });
    }
    return resp;
  };

  getWorkRoleListEntry = async (userQuizId: DbKey<DB.UserQuiz>): Promise<FE.WorkRoleListEntry> => {
    const role = await this.service.userQuizWorkRole(userQuizId);
    return role;
  };

  /// There are some race conditions with the self-assessment because we are updating multiple
  /// questions in the same call and the client isn't waiting for the updates to finish (or the server
  /// for the database). So ensure that the UserQuiz is properly finished
  finishSelfAssessment = async (): Promise<FE.UserQuizSummary> => {
    await this.service.completeUserQuiz(this.userQuizId);
    const uQSummary = await this.getUserQuizSummary();
    return uQSummary;
  };

  recreateUserQuiz = async (): Promise<void> => {
    /// Called from QuizQuestionScreen onDone handler only
    /// TODO Enable/disable at group level
    /// TODO Check for existing unfinished quizzes maybe?
    if (this.identity.appClient.recreateUserQuizEnabled) {
      if (this.userQuizSummary.kind === 'value') {
        /// FF2-355
        const userQuizId = await this.service.recreateUserQuiz(
          DB.makeUserQuiz({
            appUserId: this.identity.appUserWithDbId.id,
            quizId: this.userQuizSummary.value.quizId,
            startedAt: null,
            completedAt: null,
            dueAt: null
          })
        );
        ignoreUnused(userQuizId);
      }
    }
  };

  // TODO re-document this
  // This method differs from QuizResultsStore.getTrainingOfferingUrl as follows:
  // 1. The method directly returns the one-time link (rather than waiting for the user to click 
  //    another API to invoke it). 
  // 2. This one-time link can then act as a src resource for an embedded iframe.
  // 3. The returned HTTP response is HTTP-2xx not HTTP-303 - no redirection is required.
  getCourseInfoWithTrackedLink = async (
    offeringId: DbKey<DB.TrainingOffering>,
    userId: DbKey<DB.AppUser>
  ) => {
    const trackedLink = await this.service.getCourseInfoWithTrackedLink(offeringId, userId);
      return trackedLink;
  };

  // When running an intro/outro question with an embedded offering, we want to use the TIA Online (Scodle) 
  // 'completion' event to decide whether to allow progress to results. However, this is only posted by
  // TIA Online for Production, so to run in UAT or DEV/TEST or any other environment, we have to generate
  // this ourselves. We can do this by tying this function to an answer response. When we want to use the
  // offering in Production, we can simply remove the answer and then generate the quiz.
  generateCompletionEvent = async (courseInfo: FE.TrackedCourseInfo) => {
    const event: CompletionEvent = {
      event: 'completion',
      properties: makeCompletionEventProperties({
        courseid: courseInfo.courseId || '',
        course: courseInfo.courseName || '',
        attempt: 1,
        status: 'completed',
        trackid: 'randomTrackId',
        ip: '127.0.0.1',
        distinct_id: 'randomDistinctId',
        host: 'www.tiaonline.scodle.com',
        username: courseInfo.userName || '',
        firstname: courseInfo.firstName,
        lastname: courseInfo.lastName,
        email: courseInfo.userEmail
      })
    };

    // discard return value
    await this.service.saveSyntheticCompletionEvent(event);
  }

  fetchCompletionEvent = async (
    offeringId: DbKey<DB.TrainingOffering>,
    userId: DbKey<DB.AppUser>
  ): Promise<CompletionEvent | null> => {
    const event = this.service.getCompletionEvent(offeringId, userId);
    return event;
  }
}

// tslint:disable-next-line: no-any
function ignoreUnused(_i: any) {}



// WEBPACK FOOTER //
// ./src/stores/quiz-store.ts